import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { DeviceService } from './device.service';
import {CapacitorSQLite, SQLiteConnection, CapacitorSQLitePlugin, capSQLiteVersionUpgrade, SQLiteDBConnection} from '@capacitor-community/sqlite';


@Injectable({
  providedIn: 'root'
})
export class InDbService {
  private _plugin: CapacitorSQLitePlugin;
  private _connection: SQLiteConnection;
  private _db: SQLiteDBConnection;
  private _doneInit = false;
  private _dbName = 'tempusin_v2';
  private _currentVersion: number;
  private _upgrades: capSQLiteVersionUpgrade[] = [
    {
      toVersion: 1,
      statements: [
        `CREATE TABLE IF NOT EXISTS store_value(
          id TEXT PRIMARY KEY,
          value TEXT 
        );`
      ]
    }
  ];

  constructor(
    private device: DeviceService,
    private plt: Platform,
    private logger: NGXLogger
  ) {
    this._currentVersion = this._upgrades[this._upgrades.length - 1].toVersion;
  }

  async init() {
    await this.plt.ready();
    if (this.device.isDevice()) {
      try {
        this._plugin = CapacitorSQLite;
        this._connection = new SQLiteConnection(this._plugin);
        await this._connection.addUpgradeStatement(this._dbName, this._upgrades);
        this._db = await this.openDatabase(this._dbName, false, 'no-encryption', this._currentVersion, false);
        this._doneInit = true;
      }
      catch (err: any) {
        this.logger.error('Failed initing SQLite DB', err);
      }
    }
  }

  async keys(): Promise<string[]> {
    if (!this._doneInit) return [];

    try {
      const result = await this._db.query('SELECT id FROM store_value;');
      return result.values?.map(v => v['id']).filter(v => !!v) ?? [];
    }
    catch (err: any) {
      this.logger.error('Failed getting database keys', err);
      return [];
    }
  }

  async get(key: string): Promise<any> {
    if (!this._doneInit || !key) return null;

    try {
      const result = await this._db.query(`SELECT value FROM store_value WHERE id='${key}';`);
      let value = result.values?.length > 0 ? result.values[0]['value'] : null;
      if (value && typeof value === 'string') {
        value = JSON.parse(value);
      }
      return value;
    }
    catch (err: any) {
      this.logger.error(`Failed getting value for key '${key}'`, err);
      return null;
    }
  }

  async set(key: string, value: any): Promise<boolean> {
    if (!this._doneInit || !key || !value) return false;

    try {
      await this._db.run(`INSERT OR REPLACE INTO store_value (id, value) VALUES (?, ?);`, [key, JSON.stringify(value)]);
    }
    catch (err: any) {
      this.logger.error(`Failed setting value for key '${key}'`, err);
      return false;
    }
  }

  async remove(key: string): Promise<boolean> {
    if (!this._doneInit || !key) return false;

    try {
      await this._db.run(`DELETE FROM store_value WHERE id='${key}';`);
    }
    catch (err: any) {
      this.logger.error(`Failed deleting key '${key}'`, err);
      return false;
    }
  }

  private async openDatabase(dbName: string, encrypted: boolean, mode: string, version: number, readonly: boolean): Promise<SQLiteDBConnection> {
    let db: SQLiteDBConnection;
    const retCC = (await this._connection.checkConnectionsConsistency()).result;
    const isConn = (await this._connection.isConnection(dbName, readonly)).result;
    if (retCC && isConn) {
        db = await this._connection.retrieveConnection(dbName, readonly);
    }
    else {
        db = await this._connection.createConnection(dbName, encrypted, mode, version, readonly);
    }
    await db.open();
    return db;
  }
}
